/**
 * executive-report-brief.ts — Editorial copy layer for the Executive Report
 * standfirst.
 *
 * Mirrors `posture-brief.ts` in shape and voice rules. The Executive Report
 * tells a longer-arc story than the Command Center daily brief: we frame the
 * verdict against a configurable reporting period (30 / 60 / 90 days), not
 * "today", and the narrative wraps a board-ready summary rather than the
 * operational queue.
 *
 * Contract: every editorial slot is ONE deterministic, data-driven line.
 * There is no variant table and no random or hashed selection — the same
 * input always produces the same brief, and all variation in what the reader
 * sees comes
 * from the data (score, delta, component scores, critical count), never from
 * picking among synonymous phrasings of the same fact.
 *
 * Headline precedence (highest to lowest):
 *   1. Critical count — one or more open critical items.
 *   2. Component-laggard — one component under 70 stands out.
 *   3. Clear (score >= 85, zero critical), then cadence or queue by score.
 *
 * Loading-state handling: `weakestComponent()` refuses to name a weakest when
 * any component input is missing (undefined or null), so the brief never
 * claims a component is "the lowest" while another is still streaming in.
 * Mirrors `posture-brief.ts`'s `weakestPillar()` guard.
 *
 * Action-first fields (blueprint §6.2 row 8): `headline` follows the
 * precedence above; `lede` names the lowest component (or, without all four,
 * the Guardian Score) and the period's movement. The period delta comes from
 * `periodStartScore`, so it is the Guardian Score's movement, and the lede
 * says so rather than pinning it on the component. The emphasized component
 * never takes a pillar ink: no full component label is a pillar's display
 * name (Subject-Ink Rule, §6.3). `narrativeDeck` stays as the report body.
 */

import type { ActionBriefFields } from './brief-shape'
import type { ExecutiveReportData } from '@/types/executiveReport'

/* -------------------------------------------------------------------------- */
/* Public shape                                                               */
/* -------------------------------------------------------------------------- */

export interface ExecutiveReportBrief {
  /** Six words or fewer. The Executive Report shows it on the Guardian Score tile, upright and unquoted (D22). */
  verdict: string
  /** Byline footer with `**bold**` markers around author + tenant. */
  byline: string
  /** Score delta vs period start. The report computes its tile's delta tag itself, not from this field. */
  delta: { value: number; days: number }
  /** One helper-text line above the AI narrative (NarrativeBlock), derived deterministically. */
  narrativeDeck: string
  /** Action-first h1 (blueprint §6.2 row 8). */
  headline: string
  /** The component the headline names, set at 400 (never a pillar ink). */
  emphasis?: ActionBriefFields['emphasis']
  /** One sentence, one `**bold**` run naming the subject and its count. */
  lede: string
}

/** Compact component key — used for the verdict and headline substitutions
 *  and the weakest-component lookup. */
export type ExecutiveComponentKey = 'secure' | 'bpa' | 'mfa' | 'baseline'

/**
 * Component names for the headline, the lede and the verdict. The verdict
 * uses the same name as the headline (as `posture-brief.ts` does), never a
 * clipped one like "Practice", and its templates fit six words with the
 * three-word names.
 */
const COMPONENT_LABEL_FULL: Record<ExecutiveComponentKey, string> = {
  secure: 'Microsoft Secure Score',
  bpa: 'Best Practice analyzer',
  mfa: 'MFA adoption',
  baseline: 'Baseline compliance',
}

export interface ExecutiveReportBriefInput {
  /** Active tenant ID. Callers still pass this; unused by the composer now
   *  that copy is fully data-driven rather than picked per tenant per day. */
  tenantId?: string
  /** Friendly tenant name for the byline — falls back to "your tenant". */
  tenantName?: string
  /** Composite Guardian Score (0-100). When null we return the loading brief. */
  score?: number | null
  /** Period-start composite Guardian Score, used for the 30/60/90d delta. */
  periodStartScore?: number | null
  /** Reporting window in days. Drives the byline period chip and the delta window. */
  periodDays?: number
  /** Per-component scores, in 0-100 percentage form. Each may be null when
   *  the underlying signal hasn't arrived yet. */
  components?: {
    secureScore?: number | null
    bpa?: number | null
    mfa?: number | null
    baseline?: number | null
  }
  /** Total open critical violations — drives the headline. */
  openCriticalViolations?: number
  /** Total open high violations — not read by the composer. */
  openHighViolations?: number
  /** Whether the AI narrative is present. Drives `narrativeDeck` framing. */
  hasNarrative?: boolean
  /** Override "today" — feeds the byline date, primarily for testing. */
  asOfDate?: Date
}

/* -------------------------------------------------------------------------- */
/* Internal helpers                                                           */
/* -------------------------------------------------------------------------- */

/**
 * Returns the lowest-scoring component, but ONLY when ALL FOUR components
 * are defined numbers. Same loading-race guard `posture-brief.ts`'s
 * `weakestPillar()` uses: naming one component "the lowest" from a partial
 * set lies to the user — a component that hasn't loaded yet, or is `null`
 * because the signal doesn't apply to this tenant, could actually be lower.
 */
function weakestComponent(
  c: ExecutiveReportBriefInput['components'],
): { key: ExecutiveComponentKey; score: number } | undefined {
  if (!c) return undefined
  const candidates: { key: ExecutiveComponentKey; score: number | null | undefined }[] = [
    { key: 'secure', score: c.secureScore },
    { key: 'bpa', score: c.bpa },
    { key: 'mfa', score: c.mfa },
    { key: 'baseline', score: c.baseline },
  ]
  for (const cand of candidates) {
    if (typeof cand.score !== 'number') return undefined
  }
  let best: { key: ExecutiveComponentKey; score: number } | undefined
  for (const cand of candidates) {
    const s = cand.score as number
    if (!best || s < best.score) best = { key: cand.key, score: s }
  }
  return best
}

/** Word form for 1-9, digits above 9 — keeps the critical-count clauses
 *  reading as prose rather than a bare digit mid-sentence. */
function countWord(n: number): string {
  const words = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine']
  return n >= 1 && n <= 9 ? words[n] : String(n)
}

/** Finite, non-negative critical count, or `undefined` when the caller
 *  didn't supply one. Keeps the headline branching from treating a
 *  missing count as "zero critical items". */
function normalizeCrit(n: number | undefined): number | undefined {
  return typeof n === 'number' && Number.isFinite(n) && n >= 0 ? n : undefined
}

/** Round to one decimal, then render as an integer string when whole
 *  ("4") and one decimal place otherwise ("0.4", "2.5"). */
function formatDelta(x: number): string {
  const r = Math.round(x * 10) / 10
  return Number.isInteger(r) ? String(r) : r.toFixed(1)
}

/* -------------------------------------------------------------------------- */
/* Copy tables — one line per slot, tokens filled from data                   */
/* -------------------------------------------------------------------------- */

const VERDICT = {
  LOADING: 'Compiling the brief…',
  ROUTINE_WATCH: 'Routine watch only.',
  CRIT_STILL_ONE: 'One critical item still open.',
  CRIT_STILL_FEW: '{Count} critical items still open.',
  LIFT_NEXT: 'Lift {Full} next.',
  LEAD_NEXT: 'Take the lead item next.',
  LIFT_FIRST: 'Lift {Full} first.',
  START_LEAD: 'Start with the lead item.',
  WORK_QUEUE: 'Work the queue top down.',
}

const NARRATIVE = {
  PRESENT: 'Guardian read the period in full. The board summary follows.',
  ABSENT: 'No board summary was compiled for this period. The numbers below stand on their own.',
}

/** Action-first h1 (blueprint §6.2 row 8), critical count, then laggard. */
const HEADLINE_ACTION = {
  LOADING: 'Compiling the report.',
  CRIT_ONE: 'Close the critical item first.',
  CRIT_FEW: 'Close the {n} critical items first.',
  LAGGARD: 'Start with {Full} this sprint.',
  CLEAR: 'Nothing critical to close this period.',
  CADENCE: 'Keep the usual review cadence this period.',
  QUEUE: 'Work the queue top down this sprint.',
}

/**
 * The lede: the lowest component when all four are known, else the Guardian
 * Score, then the period's movement (only when a period-start score exists).
 */
const LEDE = {
  LOADING: '**Guardian is compiling your report**.',
  LOWEST: '**{Full} is the lowest component, at {score}**',
  SCORE: '**Your Guardian Score is {score}**',
  LOWEST_UP: ', and the Guardian Score is up {n} in {d} days',
  LOWEST_DOWN: ', and the Guardian Score is down {n} in {d} days',
  LOWEST_FLAT: ', and the Guardian Score is unchanged in {d} days',
  SCORE_UP: ', up {n} in {d} days',
  SCORE_DOWN: ', down {n} in {d} days',
  SCORE_FLAT: ', unchanged in {d} days',
}

/* -------------------------------------------------------------------------- */
/* Verdict (six words or fewer)                                               */
/* -------------------------------------------------------------------------- */

/**
 * `lead` is the component the headline leads with (the laggard, when no
 * critical item outranks it), else undefined. The verdict names only that
 * component, so the Guardian Score tile and the headline name one first
 * move (the `posture-brief.ts` coherence rule).
 */
function pickVerdict(score: number, lead: ExecutiveComponentKey | undefined, crit: number | undefined): string {
  const name = lead ? COMPONENT_LABEL_FULL[lead] : ''
  if (score >= 90) {
    if (typeof crit === 'number' && crit >= 1) {
      return crit === 1 ? VERDICT.CRIT_STILL_ONE : VERDICT.CRIT_STILL_FEW.replace('{Count}', countWord(crit))
    }
    if (lead) return VERDICT.LIFT_NEXT.replace('{Full}', name)
    return VERDICT.ROUTINE_WATCH
  }

  if (score >= 75) {
    if (lead) return VERDICT.LIFT_NEXT.replace('{Full}', name)
    return VERDICT.LEAD_NEXT
  }

  if (lead) return VERDICT.LIFT_FIRST.replace('{Full}', name)
  return score >= 60 ? VERDICT.START_LEAD : VERDICT.WORK_QUEUE
}

/* -------------------------------------------------------------------------- */
/* Narrative standfirst (frames the AI executive narrative section)            */
/* -------------------------------------------------------------------------- */

function buildNarrativeDeck(hasNarrative: boolean): string {
  return hasNarrative ? NARRATIVE.PRESENT : NARRATIVE.ABSENT
}

/* -------------------------------------------------------------------------- */
/* Action-first headline + lede (blueprint §6.2 row 8)                        */
/* -------------------------------------------------------------------------- */

function pickActionHeadline(
  laggard: boolean,
  weakest: { key: ExecutiveComponentKey; score: number } | undefined,
  crit: number | undefined,
  score: number,
): { headline: string; emphasis?: ActionBriefFields['emphasis'] } {
  if (crit === 1) return { headline: HEADLINE_ACTION.CRIT_ONE }
  if (crit !== undefined && crit >= 2) return { headline: HEADLINE_ACTION.CRIT_FEW.replace('{n}', String(crit)) }
  if (laggard && weakest) {
    const full = COMPONENT_LABEL_FULL[weakest.key]
    return { headline: HEADLINE_ACTION.LAGGARD.replace('{Full}', full), emphasis: { text: full } }
  }
  if (crit === 0 && score >= 85) return { headline: HEADLINE_ACTION.CLEAR }
  return { headline: score >= 90 ? HEADLINE_ACTION.CADENCE : HEADLINE_ACTION.QUEUE }
}

function buildLede(
  weakest: { key: ExecutiveComponentKey; score: number } | undefined,
  score: number,
  delta: { dir: 'up' | 'down' | 'flat'; n: string; days: number } | undefined,
): string {
  const head = weakest
    ? LEDE.LOWEST.replace('{Full}', COMPONENT_LABEL_FULL[weakest.key]).replace(
        '{score}',
        String(Math.round(weakest.score)),
      )
    : LEDE.SCORE.replace('{score}', String(Math.round(score)))
  if (!delta) return `${head}.`
  const clause = weakest
    ? delta.dir === 'up' ? LEDE.LOWEST_UP : delta.dir === 'down' ? LEDE.LOWEST_DOWN : LEDE.LOWEST_FLAT
    : delta.dir === 'up' ? LEDE.SCORE_UP : delta.dir === 'down' ? LEDE.SCORE_DOWN : LEDE.SCORE_FLAT
  return `${head}${clause.replace('{n}', delta.n).replace('{d}', String(delta.days))}.`
}

/* -------------------------------------------------------------------------- */
/* Public API                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Compose an ExecutiveReportBrief from the active report state. All inputs
 * are optional — the function degrades gracefully toward a plausible "no data"
 * brief rather than throwing.
 *
 * When `input.score` is null/undefined we return a neutral "Compiling…" brief
 * rather than coercing to 0 (which would land the loading state in the
 * worst-band verdict for a fraction of a second).
 *
 * `score === 0` is a legitimate value (brand-new tenant, broken connectors)
 * and still routes through the normal band logic.
 */
export function composeExecutiveReportBrief(
  input: ExecutiveReportBriefInput = {},
): ExecutiveReportBrief {
  const periodDays = typeof input.periodDays === 'number' ? input.periodDays : 30
  const tenantName = input.tenantName?.trim() ? input.tenantName : 'your tenant'
  const now = input.asOfDate ?? new Date()

  // Compute the delta from period-start when both points are present.
  const deltaValue =
    typeof input.score === 'number' && typeof input.periodStartScore === 'number'
      ? Math.round((input.score - input.periodStartScore) * 10) / 10
      : 0

  const day = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'long' }).format(now)
  const byline = `By **Guardian** · ${day} · **${tenantName}** · ${periodDays}-day period`

  if (input.score == null) {
    return {
      verdict: VERDICT.LOADING,
      byline,
      delta: { value: deltaValue, days: periodDays },
      narrativeDeck: 'Guardian is drafting the board summary.',
      headline: HEADLINE_ACTION.LOADING,
      lede: LEDE.LOADING,
    }
  }

  const score = input.score
  const dir: 'up' | 'down' | 'flat' = deltaValue > 0 ? 'up' : deltaValue < 0 ? 'down' : 'flat'
  const n = formatDelta(Math.abs(deltaValue))

  const weakest = weakestComponent(input.components)
  const laggard = !!(weakest && weakest.score < 70)
  const crit = normalizeCrit(input.openCriticalViolations)

  const action = pickActionHeadline(laggard, weakest, crit, score)
  // One first move: the verdict names the laggard only when the headline leads with it.
  const verdict = pickVerdict(score, action.emphasis && weakest ? weakest.key : undefined, crit)
  const narrativeDeck = buildNarrativeDeck(input.hasNarrative === true)

  // Only a real period-start score makes a delta; the 0 default never reads as "unchanged".
  const deltaKnown = typeof input.periodStartScore === 'number'
  const lede = buildLede(weakest, score, deltaKnown ? { dir, n, days: periodDays } : undefined)

  return {
    verdict,
    byline,
    delta: { value: deltaValue, days: periodDays },
    narrativeDeck,
    headline: action.headline,
    ...(action.emphasis ? { emphasis: action.emphasis } : {}),
    lede,
  }
}

/* -------------------------------------------------------------------------- */
/* Mapper from raw DTO → composer input                                       */
/* -------------------------------------------------------------------------- */

/**
 * Convenience adapter: collapses an `ExecutiveReportData` DTO into the brief
 * composer input. Keeps the page component free of coercion logic.
 */
export function executiveReportToBriefInput(
  report: ExecutiveReportData | null | undefined,
  opts: { tenantId?: string; tenantName?: string; periodDays?: number } = {},
): ExecutiveReportBriefInput {
  if (!report) {
    return {
      tenantId: opts.tenantId,
      tenantName: opts.tenantName,
      periodDays: opts.periodDays,
      score: null,
    }
  }
  return {
    tenantId: opts.tenantId,
    tenantName: opts.tenantName ?? report.tenantName,
    periodDays: opts.periodDays ?? report.reportPeriodDays,
    score: report.currentGuardianScore,
    periodStartScore: report.periodStartGuardianScore,
    components: {
      secureScore: report.secureScore?.current ?? null,
      bpa: report.bpaScore?.current ?? null,
      mfa: report.mfaAdoption?.current ?? null,
      baseline: report.baselineCompliance?.current ?? null,
    },
    openCriticalViolations: report.metrics?.openCriticalViolations,
    openHighViolations: report.metrics?.openHighViolations,
    hasNarrative: typeof report.executiveNarrative === 'string' && report.executiveNarrative.trim().length > 0,
  }
}

/* -------------------------------------------------------------------------- */
/* Test-only exports                                                          */
/* -------------------------------------------------------------------------- */

/** Copy tables exposed for exact-string assertions in tests (and for a
 *  generic copy-lint sweep). Not part of the public runtime API; do not
 *  import from app code. */
export const __TEST_ONLY__ = {
  VERDICT,
  NARRATIVE,
  HEADLINE_ACTION,
  LEDE,
  COMPONENT_LABEL_FULL,
}
